// Gestione utenti — lista, crea, modifica, toggle attivo, invia magic link
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Send, ToggleLeft, ToggleRight, Pencil, Eye, EyeOff } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Alert } from '../../components/ui/Alert'
import api from '../../lib/api'

interface AppUser {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  role: string
  isActive: boolean
  lastLoginAt?: string
  createdAt: string
  branchId?: string | null
}

interface Branch {
  id: string
  name: string
  city?: string
}

const ROLES = [
  { value: 'admin', label: 'Amministratore' },
  { value: 'team_leader', label: 'Responsabile Tecnici' },
  { value: 'technician', label: 'Tecnico' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'readonly', label: 'Sola lettura' },
] as const

type RoleValue = typeof ROLES[number]['value']

const userSchema = z.object({
  email: z.string().email('Email non valida'),
  firstName: z.string().min(1, 'Nome obbligatorio'),
  lastName: z.string().min(1, 'Cognome obbligatorio'),
  phone: z.string().optional(),
  role: z.enum(['admin', 'team_leader', 'technician', 'dispatcher', 'readonly']),
  branchId: z.string().uuid().optional().or(z.literal('')),
  password: z.string().min(8, 'Minimo 8 caratteri').optional().or(z.literal('')),
})

type UserFormData = z.infer<typeof userSchema>

export function UsersPage() {
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<AppUser | null>(null)
  const [inviteResult, setInviteResult] = useState<{ email: string; magicUrl: string } | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const { data: users = [], isLoading, error } = useQuery<AppUser[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get<AppUser[]>('/users')
      return res.data
    },
  })

  const { data: branches = [] } = useQuery<Branch[]>({
    queryKey: ['branches'],
    queryFn: async () => {
      const res = await api.get<Branch[]>('/companies/branches')
      return res.data ?? []
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: { email: '', firstName: '', lastName: '', phone: '', role: 'technician', branchId: '' },
  })

  const openCreate = () => {
    setEditing(null)
    reset({ email: '', firstName: '', lastName: '', phone: '', role: 'technician', branchId: '', password: '' })
    setFormOpen(true)
  }

  const openEdit = (user: AppUser) => {
    setEditing(user)
    reset({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone ?? '',
      role: (ROLES.find((r) => r.value === user.role)?.value ?? 'technician') as RoleValue,
      branchId: user.branchId ?? '',
      password: '',
    })
    setFormOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      if (editing) {
        const payload: Record<string, unknown> = {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          role: data.role,
          branchId: data.branchId || null,
        }
        if (data.password) payload['password'] = data.password
        await api.patch(`/users/${editing.id}`, payload)
      } else {
        const payload: Record<string, unknown> = { ...data, branchId: data.branchId || null }
        if (!data.password) delete payload['password']
        await api.post('/users', payload)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      setFormOpen(false)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async (userId: string) => api.patch(`/users/${userId}/toggle-active`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const inviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post<{ magicUrl: string; email: string }>('/auth/magic-link/send', { userId })
      return res.data
    },
    onSuccess: (data) => {
      setInviteResult(data)
    },
  })

  const roleLabel = (role: string) => ROLES.find((r) => r.value === role)?.label ?? role

  if (error) return <Alert variant="error">Impossibile caricare gli utenti.</Alert>

  return (
    <div className="space-y-4">
      <PageHeader
        title="Gestione Utenti"
        breadcrumb={[{ label: 'Utenti' }]}
        actions={
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
            Nuovo utente
          </Button>
        }
      />

      {inviteResult && (
        <Alert
          variant="success"
          title={`Magic link inviato a ${inviteResult.email}`}
          onDismiss={() => setInviteResult(null)}
        >
          <span className="break-all text-xs">{inviteResult.magicUrl}</span>
        </Alert>
      )}

      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
        {isLoading ? (
          <div className="py-10 text-center text-sm text-[var(--fc-foreground-muted)]">Caricamento...</div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-[var(--fc-foreground-muted)] mb-3">Nessun utente registrato.</p>
            <Button size="sm" variant="secondary" onClick={openCreate}>
              Crea il primo utente
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--fc-border)] bg-[var(--fc-surface-overlay)]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Nome</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Ruolo</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Filiale</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Stato</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Ultimo accesso</th>
                  <th className="px-4 py-3 text-right font-medium text-[var(--fc-foreground-muted)]">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--fc-border)]">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-[var(--fc-surface-overlay)]">
                    <td className="px-4 py-3 font-medium text-[var(--fc-foreground)]">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="px-4 py-3 text-[var(--fc-foreground-muted)]">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block rounded bg-[var(--fc-surface-overlay)] px-2 py-0.5 text-xs text-[var(--fc-foreground)]">
                        {roleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--fc-foreground-muted)]">
                      {user.branchId
                        ? (branches.find((b) => b.id === user.branchId)?.name ?? '—')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs font-medium ${
                          user.isActive ? 'text-[var(--fc-success)]' : 'text-[var(--fc-foreground-muted)]'
                        }`}
                      >
                        {user.isActive ? 'Attivo' : 'Inattivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--fc-foreground-muted)]">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString('it-IT')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => openEdit(user)}
                          title="Modifica"
                          aria-label="Modifica utente"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => inviteMutation.mutate(user.id)}
                          title="Invia magic link"
                          aria-label="Invia invito"
                          disabled={inviteMutation.isPending}
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => toggleMutation.mutate(user.id)}
                          title={user.isActive ? 'Disabilita utente' : 'Abilita utente'}
                          aria-label={user.isActive ? 'Disabilita utente' : 'Abilita utente'}
                        >
                          {user.isActive ? (
                            <ToggleRight className="h-3.5 w-3.5 text-[var(--fc-success)]" />
                          ) : (
                            <ToggleLeft className="h-3.5 w-3.5 text-[var(--fc-foreground-muted)]" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crea / modifica */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? `Modifica: ${editing.firstName} ${editing.lastName}` : 'Nuovo utente'}
        size="md"
      >
        <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nome *"
              {...register('firstName')}
              error={errors.firstName?.message}
              placeholder="Mario"
            />
            <Input
              label="Cognome *"
              {...register('lastName')}
              error={errors.lastName?.message}
              placeholder="Rossi"
            />
          </div>
          <Input
            label="Email *"
            type="email"
            {...register('email')}
            error={errors.email?.message}
            placeholder="mario.rossi@azienda.it"
            disabled={Boolean(editing)}
          />
          <Input
            label="Telefono"
            {...register('phone')}
            placeholder="+39 348 0000000"
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Ruolo *</label>
            <select
              {...register('role')}
              className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {errors.role && <p className="text-xs text-[var(--fc-danger)]">{errors.role.message}</p>}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Filiale</label>
            <select
              {...register('branchId')}
              className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
            >
              <option value="">— Nessuna filiale —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}{b.city ? ` (${b.city})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Input
              label={editing ? 'Nuova password (lascia vuoto per non cambiare)' : 'Password *'}
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              error={errors.password?.message}
              placeholder="Minimo 8 caratteri"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {saveMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio. Riprova.</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting || saveMutation.isPending}>
              {saveMutation.isPending ? 'Salvataggio...' : editing ? 'Salva modifiche' : 'Crea utente'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
